import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import JobContainer from 'components/jobs/container/JobContainer';
import NoSSR from 'components/noSSR';
import { dispatchCreateJobBucket } from 'store/jobs/actions';
import { getStateAbbr, getUserOrGeoLocations } from 'utils';
import { JOB_BUCKET_GROUP, PAID_JOBS_CATEGORY } from 'utils/constant';

import filterList from '../../../utils/filter';
import EmploymentStatistics from '../employmentStatistics';

import AgeBreakdown from './AgeBreakdown';
import AgeByEthnicityGender from './AgeByEthnicityGender';
import DegreeLevelTypes from './degreeLevelTypes';
import GenderStatistics from './genderStatistics';
import RaceStatistics from './raceStatistics';
import TurnoverEmploymentStatistics from './turnoverEmploymentStatistics';

import classes from './diversitySection.module.css';

const ANY_JOB_BUCKET_FOR_PAID_JOB_BUCKET_GROUP = JOB_BUCKET_GROUP.CAREER_SKILLS_JOBS_BUCKET;

const DiversitySection = ({
  criteria,
  ages,
  durations,
  jobTitle,
  genderRatio,
  genderOverTime,
  demographicPieChartData,
  raceEthnicitiesOverTime,
  employmentOverTime,
  articles,
  lgbt,
  lgbtSkewTitles,
  salaryEstimates,
  ageByEthnicityGender,
  filterLogEventCallback,
  jobsIndex,
  salaryIndex,
  educationIndex,
  jobContainerFilters,
  userLocation,
  jobBucket,
  isDesktop,
  demographicsData,
  inDemandJobs,
}) => {
  const dispatch = useDispatch();

  const user = useSelector(({ user }) => user);

  const { pieChartData } = demographicPieChartData;

  const agesRangePresent = ages && ages.range && ages.range.length > 0;

  const genderOverTimePresent = genderOverTime && genderOverTime.length > 0;

  const raceEthnicitiesOverTimePresent =
    raceEthnicitiesOverTime && raceEthnicitiesOverTime.length > 0;

  const salaryEstimatesPresent = salaryEstimates && !_isEmpty(salaryEstimates);

  const ageByEthnicityGenderPresent = ageByEthnicityGender && !_isEmpty(ageByEthnicityGender);

  // Content Blurbs

  // const diversitySectionContentBlurb = articles.find(article => article.post_subtype === 'diversity-section');

  const genderSectionContentBlurb = articles.find(
    (article) => article.post_subtype === 'gender-section'
  );

  const mostCommonDegreesContentBlurb = articles.find(
    (article) => article.post_subtype === 'intro-section-degrees'
  );

  const ethnicitySectionContentBlurb = articles.find(
    (article) => article.post_subtype === 'ethnicity-section'
  );

  const foreignLanguageContentBlurb = articles.find(
    (article) => article.post_subtype === 'foreign-languages-spoken-section'
  );

  const ethnicityOverTimeContentBlurb = articles.find(
    (article) => article.post_subtype === 'ethnicity-over-time'
  );

  const genderOverTimeContentBlurb = articles.find(
    (article) => article.post_subtype === 'gender-over-time'
  );

  const unemploymentRateContentBlurb = articles.find(
    (article) => article.post_subtype === 'unemployment-rate'
  );

  const yearsAtCurrentJobContentBlurb = articles.find(
    (article) => article.post_subtype === 'years-at-current-job-section'
  );

  const ageBreakDownContentBlurb = articles.find(
    (article) => article.post_subtype === 'workforce-age-section'
  );

  const compareGenderContentBlurb = articles.find(
    (article) => article.post_subtype === 'compare-gender-section'
  );

  const lgbtRatioContentBlurb = articles.find((article) => article.post_subtype === 'lgbt-ratio');

  const ageByEthinictyGenderContentBlurb = articles.find(
    (article) => article.post_subtype === 'age-by-race-gender'
  );

  const ethnicityIncomeBlurb = articles.find(
    (article) => article.post_subtype === 'salaries-by-race'
  );

  const educationIncomeBlurb = articles.find(
    (article) => article.post_subtype === 'salaries-by-education'
  );

  const { relatedTitles, jobTitleArticle } = criteria;

  const relatedTitlesValueRange =
    relatedTitles && relatedTitles.length > 0 ? filterList.compareJobs(relatedTitles) : [];

  const getUserLocations = () => {
    let locations = [];
    // use userLocation
    if (userLocation && userLocation.length) {
      locations = userLocation.map((val) => ({
        city: val.city,
        state: val.city ? val.state : getStateAbbr(val.state),
      }));
      // use criteria
    } else if (criteria && criteria.geoCity && criteria.geoStateCode) {
      locations = [
        {
          city: criteria.geoCity,
          state: criteria.geoStateCode,
        },
      ];
    }
    return locations;
  };

  const createPaidJobAPIPayload = () => {
    const payload = {
      category: PAID_JOBS_CATEGORY.CAREER_CLUSTER,
      titles: [{ normalizedTitle: criteria.title, originalSearch: criteria.title }],
      locations: getUserLocations(),
      numJobs: 4,
    };
    return payload;
  };

  useEffect(() => {
    dispatch(dispatchCreateJobBucket({ key: ANY_JOB_BUCKET_FOR_PAID_JOB_BUCKET_GROUP }));
  }, []);

  const degreePieChart =
    pieChartData && pieChartData.find(({ key, showPieChart }) => key === 'degree' && showPieChart);
  const languagePieChart =
    pieChartData &&
    pieChartData.find(({ key, showPieChart }) => key === 'languages' && showPieChart);
  const ethnicityPieChart =
    pieChartData &&
    pieChartData.find(({ key, showPieChart }) => key === 'ethnicities' && showPieChart);
  const genderPieChart =
    pieChartData && pieChartData.find(({ key, showPieChart }) => key === 'genders' && showPieChart);

  const titleJobsBucket = (
    <NoSSR>
      <div className="section-separator-large-full-width" />
      <JobContainer
        layout="bucket"
        title={`${criteria.title} JOBS`}
        initialPayload={{
          title: criteria.title,
          paidJobs: true,
          locations: getUserOrGeoLocations([], criteria), // _get(user, 'userLocation', [])
        }}
        userLocation={_get(user, 'userLocation', [])}
        criteria={criteria}
        jobGroup={ANY_JOB_BUCKET_FOR_PAID_JOB_BUCKET_GROUP} // Here we used any random group for this
        isDesktop={isDesktop}
        filters={jobContainerFilters}
        filterGroup={ANY_JOB_BUCKET_FOR_PAID_JOB_BUCKET_GROUP}
      />
    </NoSSR>
  );

  return (
    <section id="diversity-setion" className={classes['diversity-section-container']}>
      <GenderStatistics
        jobTitle={jobTitle}
        jobTitlePlural={criteria.jobTitlePlural}
        genderPieChart={genderPieChart}
        genderSectionContentBlurb={genderSectionContentBlurb}
        salaryEstimates={salaryEstimatesPresent && salaryEstimates}
        genderOverTime={genderOverTimePresent && genderOverTime}
        genderOverTimeContentBlurb={genderOverTimeContentBlurb}
        filterLogEventCallback={filterLogEventCallback}
        filterValueRange={relatedTitlesValueRange}
        genderRatio={genderRatio}
        compareGenderContentBlurb={compareGenderContentBlurb}
        jobsIndex={jobsIndex}
      />

      {isDesktop === false ? (
        <div className={classNames('mb-5', classes['demographics-jobs'])}>{titleJobsBucket}</div>
      ) : null}

      <RaceStatistics
        jobTitle={jobTitle}
        raceEthnicitiesOverTime={raceEthnicitiesOverTimePresent && raceEthnicitiesOverTime}
        ethnicityOverTimeContentBlurb={ethnicityOverTimeContentBlurb}
        salaryEstimates={salaryEstimatesPresent && salaryEstimates}
        ethnicityIncomeBlurb={ethnicityIncomeBlurb}
        ethnicitySectionContentBlurb={ethnicitySectionContentBlurb}
        ethnicityPieChart={ethnicityPieChart}
        filterLogEventCallback={filterLogEventCallback}
        filterValueRange={relatedTitlesValueRange}
        isDesktop={isDesktop}
        inDemandJobs={inDemandJobs}
        salaryIndex={salaryIndex}
      />

      {isDesktop === true ? (
        <div className={classes['demographics-jobs']}>{titleJobsBucket}</div>
      ) : (
        <></>
      )}

      <div className="section-separator-large-full-width" />

      <section id="age-statistics" className="w-730 mt-5">
        {ageByEthnicityGenderPresent && (
          <AgeByEthnicityGender
            ageByEthnicityGender={ageByEthnicityGender}
            contentBlurb={ageByEthinictyGenderContentBlurb}
            jobTitleArticle={jobTitleArticle}
            jobTitle={jobTitle}
          />
        )}

        {agesRangePresent && (
          <AgeBreakdown
            filterLogEventCallback={filterLogEventCallback}
            filterValueRange={relatedTitlesValueRange}
            jobTitle={jobTitle}
            ages={ages.range}
            ageBreakDownContentBlurb={ageBreakDownContentBlurb}
          />
        )}
      </section>

      <div className="section-separator-large-full-width" />

      <DegreeLevelTypes
        jobTitle={jobTitle}
        jobTitlePlural={criteria.jobTitlePlural}
        salaryEstimates={salaryEstimatesPresent && salaryEstimates}
        educationIncomeBlurb={educationIncomeBlurb}
        mostCommonDegreesContentBlurb={mostCommonDegreesContentBlurb}
        degreePieChart={degreePieChart}
        filterLogEventCallback={filterLogEventCallback}
        filterValueRange={relatedTitlesValueRange}
        educationIndex={educationIndex}
        locations={getUserLocations()}
      />

      <div className="section-separator-large-full-width" />

      <section id="employment-statistics">
        <EmploymentStatistics
          filterLogEventCallback={filterLogEventCallback}
          criteria={criteria}
          jobTitle={criteria.title}
          jobTitlePlural={criteria.jobTitlePlural}
          articles={articles}
          demographicsData={demographicsData}
        />
      </section>

      <TurnoverEmploymentStatistics
        jobTitle={jobTitle}
        employmentOverTime={employmentOverTime}
        unemploymentRateContentBlurb={unemploymentRateContentBlurb}
        filterLogEventCallback={filterLogEventCallback}
        filterValueRange={relatedTitlesValueRange}
        durations={durations}
        yearsAtCurrentJobContentBlurb={yearsAtCurrentJobContentBlurb}
        lgbt={lgbt}
        lgbtSkewTitles={lgbtSkewTitles}
        lgbtRatioContentBlurb={lgbtRatioContentBlurb}
        languagePieChart={languagePieChart}
        foreignLanguageContentBlurb={foreignLanguageContentBlurb}
      />

      {!jobBucket && (
        <section data-title="JOBS" id="jobs" className={classes['demographics-jobs']}>
          <div className="section-separator-large" />
          <JobContainer
            layout="bucket"
            title={`${jobTitle} Jobs`}
            filters={jobContainerFilters}
            filterGroup={JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_JOBS_BUCKET}
            jobGroup={JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_JOBS_BUCKET}
            initialPayload={{
              title: jobTitle,
            }}
            criteria={criteria}
            userLocation={userLocation}
            isDesktop={isDesktop}
            paidJobAPIPayload={createPaidJobAPIPayload()}
            hideMoreBtn
          />
        </section>
      )}
    </section>
  );
};

export default DiversitySection;
